import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { TOOLS } from "@/constants/testIds";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Area,
  AreaChart,
} from "recharts";

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

const RENDIMIENTO_ANUAL = 0.07; // 7% anual conservador
const EDAD_RETIRO = 65;

const projectRetirement = (edad, ahorro, aporte) => {
  const years = Math.max(EDAD_RETIRO - edad, 1);
  const arr = [];
  let saldo = ahorro;
  for (let y = 0; y <= years; y++) {
    arr.push({ edad: edad + y, saldo: Math.round(saldo) });
    saldo = saldo * (1 + RENDIMIENTO_ANUAL) + aporte * 12;
  }
  return arr;
};

const RetirementCalculator = () => {
  const [edad, setEdad] = useState(35);
  const [ahorro, setAhorro] = useState(100000);
  const [aporte, setAporte] = useState(5000);

  const series = useMemo(
    () => projectRetirement(edad, ahorro, aporte),
    [edad, ahorro, aporte]
  );

  const totalFinal = series[series.length - 1]?.saldo ?? 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5 space-y-10">
        <div>
          <div className="eyebrow text-razmek-gold">Calculadora de retiro</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-razmek-dark">
            Tu capital al cumplir <span className="italic text-razmek-blue">65 años</span>.
          </h2>
          <p className="mt-4 font-sans text-sm text-razmek-dark/60 max-w-sm">
            Proyección con rendimiento conservador del 7% anual. Modelo
            informativo, no constituye recomendación personalizada.
          </p>
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Edad actual</Label>
            <span className="font-serif text-2xl text-razmek-blue">{edad}</span>
          </div>
          <Slider
            data-testid={TOOLS.retiroEdad}
            min={20}
            max={60}
            step={1}
            value={[edad]}
            onValueChange={(v) => setEdad(v[0])}
          />
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Ahorro actual</Label>
            <span className="font-serif text-2xl text-razmek-blue">
              {fmtMXN(ahorro)}
            </span>
          </div>
          <Slider
            data-testid={TOOLS.retiroAhorro}
            min={0}
            max={5000000}
            step={10000}
            value={[ahorro]}
            onValueChange={(v) => setAhorro(v[0])}
          />
        </div>
        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Aporte mensual</Label>
            <span className="font-serif text-2xl text-razmek-blue">
              {fmtMXN(aporte)}
            </span>
          </div>
          <Slider
            data-testid={TOOLS.retiroAporte}
            min={0}
            max={100000}
            step={500}
            value={[aporte]}
            onValueChange={(v) => setAporte(v[0])}
          />
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="border border-razmek p-6 lg:p-8">
          <div className="eyebrow text-razmek-gray">Capital proyectado</div>
          <div
            data-testid={TOOLS.retiroResult}
            className="font-serif text-5xl lg:text-6xl text-razmek-blue mt-3"
          >
            {fmtMXN(totalFinal)}
          </div>
          <div className="eyebrow text-razmek-gold mt-2">
            al cumplir {EDAD_RETIRO} años
          </div>

          <div className="h-[300px] w-full mt-8" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D89737" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D89737" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(29,46,82,0.08)" vertical={false} />
                <XAxis
                  dataKey="edad"
                  tick={{ fill: "#96999A", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(29,46,82,0.15)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#96999A", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={80}
                  tickFormatter={(v) =>
                    v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${v / 1000}K`
                  }
                />
                <Tooltip
                  contentStyle={{
                    background: "#131F28",
                    border: "1px solid #D89737",
                    color: "#fff",
                    fontFamily: "Outfit",
                    fontSize: 12,
                  }}
                  formatter={(v) => fmtMXN(v)}
                  labelFormatter={(l) => `Edad ${l}`}
                />
                <Area
                  type="monotone"
                  dataKey="saldo"
                  stroke="#D89737"
                  strokeWidth={2}
                  fill="url(#goldArea)"
                />
                <Line
                  type="monotone"
                  dataKey="saldo"
                  stroke="#1D2E52"
                  strokeWidth={1.5}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;
